import {
  canonicalTreeEdgePairKey,
  type CanonicalTreeEdgeKind,
  type CanonicalTreeRelationshipEdge,
} from './CanonicalTreeRelationshipEdge';

export const CANONICAL_KINSHIP_RULE_VERSION =
  1 as const;

export const DERIVED_AFFINITY_MAX_PARTNERSHIP_HOPS =
  1 as const;

export const
HALF_SIBLING_PARENT_SET_COMPLETENESS_AUTHORITY_AVAILABLE =
  false as const;

export type CanonicalKinshipPathTraversal =
  | 'forward'
  | 'reverse'
  | 'symmetric'
  | 'child-mediated';

export interface CanonicalKinshipPathStep {
  readonly fromMemberId: string;
  readonly toMemberId: string;
  readonly edgeId: string;
  readonly edgeKind: CanonicalTreeEdgeKind;
  readonly traversal: CanonicalKinshipPathTraversal;
}

export type CanonicalKinshipRelationshipClass =
  | 'self'
  | 'structural'
  | 'active-partnership'
  | 'derived-step'
  | 'derived-in-law'
  | 'co-parent'
  | 'direct-affiliation'
  | 'unknown';

export type CanonicalKinshipDerivationKind =
  | 'self'
  | 'structural-path'
  | 'active-partnership-edge'
  | 'bounded-partnership-composition'
  | 'shared-parentage'
  | 'direct-governed-edge'
  | 'unresolved';

export interface CanonicalKinshipDescriptor {
  readonly code: string;
  readonly preciseLabel: string;
  readonly treeLabel: string;
  readonly relationshipClass:
    CanonicalKinshipRelationshipClass;
  readonly derivationKind:
    CanonicalKinshipDerivationKind;
  readonly affinityHopCount: number;
  readonly evidenceEdgeIds: readonly string[];
  readonly connectionPath:
    readonly CanonicalKinshipPathStep[];
}

export interface CanonicalKinshipSemantics {
  readonly ruleVersion:
    typeof CANONICAL_KINSHIP_RULE_VERSION;
  readonly primary: CanonicalKinshipDescriptor;
  readonly facets:
    readonly CanonicalKinshipDescriptor[];
}

export interface CanonicalKinshipSemanticIndexes {
  readonly parentIdsByChild:
    ReadonlyMap<string, readonly string[]>;
  readonly childIdsByParent:
    ReadonlyMap<string, readonly string[]>;
  readonly parentEdgeIdByPair:
    ReadonlyMap<string, string>;
  readonly directEdgesByPair:
    ReadonlyMap<
      string,
      readonly CanonicalTreeRelationshipEdge[]
    >;
}

export interface DeriveCanonicalKinshipSemanticsInput {
  readonly viewerId: string;
  readonly memberId: string;
  readonly connectionPaths:
    readonly (
      readonly CanonicalKinshipPathStep[]
    )[];
  readonly indexes:
    CanonicalKinshipSemanticIndexes;
}

const terminalNontransitiveKinds =
  new Set<CanonicalTreeEdgeKind>([
    'co-parent',
    'step-family',
    'in-law',
    'guardian',
    'foster-family',
    'chosen-family',
    'god-family',
    'affiliated-family',
  ]);

const activeParentStatuses =
  new Set<
    CanonicalTreeRelationshipEdge['status']
  >([
    'active',
    'not-applicable',
  ]);

const parentPairKey = (
  parentId: string,
  childId: string,
): string =>
  JSON.stringify([
    parentId,
    childId,
  ]);

const uniqueSorted = (
  values: readonly string[],
): readonly string[] =>
  Object.freeze(
    Array.from(
      new Set(values),
    ).sort(),
  );

const freezePath = (
  path:
    readonly CanonicalKinshipPathStep[],
): readonly CanonicalKinshipPathStep[] =>
  Object.freeze(
    path.map(
      step =>
        Object.freeze({
          ...step,
        }),
    ),
  );

const descriptor = (
  input: {
    readonly code: string;
    readonly preciseLabel: string;
    readonly treeLabel: string;
    readonly relationshipClass:
      CanonicalKinshipRelationshipClass;
    readonly derivationKind:
      CanonicalKinshipDerivationKind;
    readonly affinityHopCount?: number;
    readonly evidenceEdgeIds?:
      readonly string[];
    readonly connectionPath?:
      readonly CanonicalKinshipPathStep[];
  },
): CanonicalKinshipDescriptor =>
  Object.freeze({
    code:
      input.code,
    preciseLabel:
      input.preciseLabel,
    treeLabel:
      input.treeLabel,
    relationshipClass:
      input.relationshipClass,
    derivationKind:
      input.derivationKind,
    affinityHopCount:
      input.affinityHopCount ?? 0,
    evidenceEdgeIds:
      uniqueSorted(
        input.evidenceEdgeIds ?? [],
      ),
    connectionPath:
      freezePath(
        input.connectionPath ?? [],
      ),
  });

const isUsableParentEdge = (
  edge: CanonicalTreeRelationshipEdge,
): boolean =>
  edge.kind === 'parent-child' &&
  edge.createsAncestry === true &&
  activeParentStatuses.has(
    edge.status,
  ) &&
  edge.confirmation !== 'rejected';

export const
isCanonicalKinshipTerminalEdgeKind = (
  kind: CanonicalTreeEdgeKind,
): boolean =>
  terminalNontransitiveKinds.has(kind);

const isStructuralStep = (
  step: CanonicalKinshipPathStep,
): boolean =>
  step.edgeKind === 'parent-child' ||
  step.edgeKind === 'sibling';

const isForwardParentStep = (
  step: CanonicalKinshipPathStep,
): boolean =>
  step.edgeKind === 'parent-child' &&
  step.traversal === 'forward';

const isReverseParentStep = (
  step: CanonicalKinshipPathStep,
): boolean =>
  step.edgeKind === 'parent-child' &&
  step.traversal === 'reverse';

const isCanonicalStructuralTraversalPath = (
  path:
    readonly CanonicalKinshipPathStep[],
): boolean => {
  let seenSibling = false;
  let seenForward = false;

  for (const step of path) {
    if (isReverseParentStep(step)) {
      if (
        seenSibling ||
        seenForward
      ) {
        return false;
      }

      continue;
    }

    if (step.edgeKind === 'sibling') {
      if (
        seenSibling ||
        seenForward
      ) {
        return false;
      }

      seenSibling = true;
      continue;
    }

    if (isForwardParentStep(step)) {
      seenForward = true;
      continue;
    }

    return false;
  }

  return true;
};

const isCanonicalChildMediatedCoParentBridge = (
  path:
    readonly CanonicalKinshipPathStep[],
): boolean =>
  path.length === 2 &&
  isForwardParentStep(
    path[0],
  ) &&
  isReverseParentStep(
    path[1],
  ) &&
  path[0].toMemberId ===
    path[1].fromMemberId;

interface CanonicalStructuralSiblingPivotShape {
  readonly up: number;
  readonly down: number;
}

const structuralSiblingPivotShape = (
  path:
    readonly CanonicalKinshipPathStep[],
): CanonicalStructuralSiblingPivotShape | null => {
  const siblingIndexes =
    path
      .map(
        (step, index) =>
          step.edgeKind === 'sibling'
            ? index
            : -1,
      )
      .filter(index => index >= 0);

  if (siblingIndexes.length !== 1) {
    return null;
  }

  const siblingIndex =
    siblingIndexes[0];

  const prefix =
    path.slice(
      0,
      siblingIndex,
    );

  const suffix =
    path.slice(
      siblingIndex + 1,
    );

  if (
    !prefix.every(
      isReverseParentStep,
    ) ||
    !suffix.every(
      isForwardParentStep,
    )
  ) {
    return null;
  }

  return Object.freeze({
    up:
      prefix.length,
    down:
      suffix.length,
  });
};

/**
 * Reachability admission grammar.
 *
 * Structural recursion is not generation capped.
 * Affinity is deliberately bounded:
 *
 * - maximum one active partnership hop;
 * - direct non-transitive relationship endpoints
 *   are reachable but terminal;
 * - a partnership in the middle of a path is
 *   accepted only for the bounded step-family
 *   shapes required by the semantic contract.
 */
export const
isCanonicalKinshipTraversalPathAdmissible = (
  path:
    readonly CanonicalKinshipPathStep[],
): boolean => {
  if (path.length === 0) {
    return true;
  }

  const terminalIndexes =
    path
      .map(
        (step, index) =>
          isCanonicalKinshipTerminalEdgeKind(
            step.edgeKind,
          )
            ? index
            : -1,
      )
      .filter(index => index >= 0);

  if (terminalIndexes.length > 0) {
    if (
      terminalIndexes.length !== 1 ||
      terminalIndexes[0] !==
        path.length - 1
    ) {
      return false;
    }

    return isCanonicalStructuralTraversalPath(
      path.slice(
        0,
        path.length - 1,
      ),
    );
  }

  const partnershipIndexes =
    path
      .map(
        (step, index) =>
          step.edgeKind ===
          'spouse-partner'
            ? index
            : -1,
      )
      .filter(index => index >= 0);

  if (
    partnershipIndexes.length >
    DERIVED_AFFINITY_MAX_PARTNERSHIP_HOPS
  ) {
    return false;
  }

  if (partnershipIndexes.length === 0) {
    return (
      isCanonicalStructuralTraversalPath(
        path,
      ) ||
      isCanonicalChildMediatedCoParentBridge(
        path,
      )
    );
  }

  const partnershipIndex =
    partnershipIndexes[0];

  if (
    !path.every(
      step =>
        isStructuralStep(step) ||
        step.edgeKind ===
          'spouse-partner',
    )
  ) {
    return false;
  }

  if (partnershipIndex === 0) {
    return isCanonicalStructuralTraversalPath(
      path.slice(1),
    );
  }

  if (
    partnershipIndex ===
      path.length - 1
  ) {
    return isCanonicalStructuralTraversalPath(
      path.slice(
        0,
        path.length - 1,
      ),
    );
  }

  const prefix =
    path.slice(
      0,
      partnershipIndex,
    );

  const suffix =
    path.slice(
      partnershipIndex + 1,
    );

  const forwardDerivedStepShape =
    prefix.length === 1 &&
    suffix.length > 0 &&
    suffix.every(
      isForwardParentStep,
    ) &&
    (
      prefix[0].edgeKind ===
        'sibling' ||
      isReverseParentStep(
        prefix[0],
      )
    );

  /*
   * Reciprocal step-nibling grammar:
   *
   *   sibling -> spouse -> descendants
   *
   * reverses to:
   *
   *   descendant -> ancestors -> spouse -> sibling
   *
   * The structural prefix may therefore contain one or more reverse
   * parent edges, followed by exactly one partnership and one terminal
   * sibling edge.
   */
  const reverseStepNiblingShape =
    prefix.length > 0 &&
    prefix.every(
      isReverseParentStep,
    ) &&
    suffix.length === 1 &&
    suffix[0].edgeKind ===
      'sibling';

  return (
    forwardDerivedStepShape ||
    reverseStepNiblingShape
  );
};

const freezeStringSetMap = (
  source:
    ReadonlyMap<string, Set<string>>,
): ReadonlyMap<
  string,
  readonly string[]
> =>
  new Map(
    Array.from(
      source.entries(),
    ).map(
      ([key, values]) =>
        [
          key,
          Object.freeze(
            [...values].sort(),
          ),
        ] as const,
    ),
  );

export const
buildCanonicalKinshipSemanticIndexes = (
  edges:
    readonly CanonicalTreeRelationshipEdge[],
): CanonicalKinshipSemanticIndexes => {
  const parentIdsByChild =
    new Map<string, Set<string>>();

  const childIdsByParent =
    new Map<string, Set<string>>();

  const parentEdgeIdByPair =
    new Map<string, string>();

  const directEdgesByPair =
    new Map<
      string,
      CanonicalTreeRelationshipEdge[]
    >();

  for (const edge of edges) {
    const pair =
      canonicalTreeEdgePairKey(
        edge.sourceMemberId,
        edge.targetMemberId,
      );

    const pairEdges =
      directEdgesByPair.get(pair) ??
      [];

    pairEdges.push(edge);

    directEdgesByPair.set(
      pair,
      pairEdges,
    );

    if (!isUsableParentEdge(edge)) {
      continue;
    }

    const parents =
      parentIdsByChild.get(
        edge.targetMemberId,
      ) ??
      new Set<string>();

    parents.add(
      edge.sourceMemberId,
    );

    parentIdsByChild.set(
      edge.targetMemberId,
      parents,
    );

    const children =
      childIdsByParent.get(
        edge.sourceMemberId,
      ) ??
      new Set<string>();

    children.add(
      edge.targetMemberId,
    );

    childIdsByParent.set(
      edge.sourceMemberId,
      children,
    );

    parentEdgeIdByPair.set(
      parentPairKey(
        edge.sourceMemberId,
        edge.targetMemberId,
      ),
      edge.id,
    );
  }

  const frozenDirectEdges =
    new Map<
      string,
      readonly CanonicalTreeRelationshipEdge[]
    >(
      Array.from(
        directEdgesByPair.entries(),
      ).map(
        ([key, values]) =>
          [
            key,
            Object.freeze(
              [...values].sort(
                (left, right) =>
                  left.id.localeCompare(
                    right.id,
                  ),
              ),
            ),
          ] as const,
      ),
    );

  return Object.freeze({
    parentIdsByChild:
      freezeStringSetMap(
        parentIdsByChild,
      ),
    childIdsByParent:
      freezeStringSetMap(
        childIdsByParent,
      ),
    parentEdgeIdByPair,
    directEdgesByPair:
      frozenDirectEdges,
  });
};

const pathEdgeIds = (
  path:
    readonly CanonicalKinshipPathStep[],
): readonly string[] =>
  uniqueSorted(
    path.map(step => step.edgeId),
  );

const greatLabel = (
  greatCount: number,
  base: string,
): string => {
  if (greatCount <= 0) {
    return base;
  }

  if (greatCount === 1) {
    return `Great-${base}`;
  }

  return `${greatCount}x Great-${base}`;
};

const ordinal = (
  value: number,
): string => {
  const mod100 =
    value % 100;

  if (
    mod100 >= 11 &&
    mod100 <= 13
  ) {
    return `${value}th`;
  }

  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
};

const removalLabel = (
  value: number,
): string => {
  if (value <= 0) {
    return '';
  }

  if (value === 1) {
    return ' once removed';
  }

  if (value === 2) {
    return ' twice removed';
  }

  return ` ${value} times removed`;
};

const structuralDescriptor = (
  path:
    readonly CanonicalKinshipPathStep[],
): CanonicalKinshipDescriptor | null => {
  const evidenceEdgeIds =
    pathEdgeIds(path);

  if (path.length === 0) {
    return descriptor({
      code:
        'self',
      preciseLabel:
        'Self',
      treeLabel:
        'Self',
      relationshipClass:
        'self',
      derivationKind:
        'self',
      connectionPath:
        path,
    });
  }

  const siblingPivot =
    structuralSiblingPivotShape(
      path,
    );

  if (siblingPivot) {
    const {
      up,
      down,
    } = siblingPivot;

    if (
      up === 0 &&
      down === 0
    ) {
      return descriptor({
        code:
          'sibling',
        preciseLabel:
          'Sibling',
        treeLabel:
          'Sibling',
        relationshipClass:
          'structural',
        derivationKind:
          'structural-path',
        evidenceEdgeIds,
        connectionPath:
          path,
      });
    }

    /*
     * viewer -> sibling -> descendant
     */
    if (up === 0) {
      const greatCount =
        down - 1;

      return descriptor({
        code:
          greatCount === 0
            ? 'nibling'
            : 'great-nibling',
        preciseLabel:
          greatLabel(
            greatCount,
            'Niece / Nephew',
          ),
        treeLabel:
          greatLabel(
            greatCount,
            'Niece / Nephew',
          ),
        relationshipClass:
          'structural',
        derivationKind:
          'structural-path',
        evidenceEdgeIds,
        connectionPath:
          path,
      });
    }

    /*
     * viewer -> ancestor -> ancestor's sibling
     */
    if (down === 0) {
      const greatCount =
        up - 1;

      return descriptor({
        code:
          greatCount === 0
            ? 'aunt-uncle'
            : 'great-aunt-uncle',
        preciseLabel:
          greatLabel(
            greatCount,
            'Aunt / Uncle',
          ),
        treeLabel:
          greatLabel(
            greatCount,
            'Aunt / Uncle',
          ),
        relationshipClass:
          'structural',
        derivationKind:
          'structural-path',
        evidenceEdgeIds,
        connectionPath:
          path,
      });
    }

    /*
     * viewer -> ancestor -> sibling -> descendant
     *
     * If the sibling pivot occurs `up` generations above the viewer and
     * the target is `down` generations below that sibling:
     *
     * cousin degree = min(up, down)
     * removal       = abs(up - down)
     */
    const degree =
      Math.min(
        up,
        down,
      );

    const removal =
      Math.abs(
        up - down,
      );

    return descriptor({
      code:
        'cousin',
      preciseLabel:
        `${ordinal(degree)} Cousin${removalLabel(removal)}`,
      treeLabel:
        'Cousin',
      relationshipClass:
        'structural',
      derivationKind:
        'structural-path',
      evidenceEdgeIds,
      connectionPath:
        path,
    });
  }

  if (
    !path.every(
      step =>
        step.edgeKind ===
          'parent-child',
    )
  ) {
    return null;
  }

  let seenForward = false;
  let up = 0;
  let down = 0;

  for (const step of path) {
    if (
      step.traversal ===
      'reverse'
    ) {
      if (seenForward) {
        return null;
      }

      up += 1;
      continue;
    }

    if (
      step.traversal ===
      'forward'
    ) {
      seenForward = true;
      down += 1;
      continue;
    }

    return null;
  }

  if (
    up > 0 &&
    down === 0
  ) {
    if (up === 1) {
      return descriptor({
        code:
          'parent',
        preciseLabel:
          'Parent',
        treeLabel:
          'Parent',
        relationshipClass:
          'structural',
        derivationKind:
          'structural-path',
        evidenceEdgeIds,
        connectionPath:
          path,
      });
    }

    if (up === 2) {
      return descriptor({
        code:
          'grandparent',
        preciseLabel:
          'Grandparent',
        treeLabel:
          'Grandparent',
        relationshipClass:
          'structural',
        derivationKind:
          'structural-path',
        evidenceEdgeIds,
        connectionPath:
          path,
      });
    }

    const greatCount =
      up - 2;

    return descriptor({
      code:
        'great-grandparent',
      preciseLabel:
        greatLabel(
          greatCount,
          'Grandparent',
        ),
      treeLabel:
        greatLabel(
          greatCount,
          'Grandparent',
        ),
      relationshipClass:
        'structural',
      derivationKind:
        'structural-path',
      evidenceEdgeIds,
      connectionPath:
        path,
    });
  }

  if (
    up === 0 &&
    down > 0
  ) {
    if (down === 1) {
      return descriptor({
        code:
          'child',
        preciseLabel:
          'Child',
        treeLabel:
          'Child',
        relationshipClass:
          'structural',
        derivationKind:
          'structural-path',
        evidenceEdgeIds,
        connectionPath:
          path,
      });
    }

    if (down === 2) {
      return descriptor({
        code:
          'grandchild',
        preciseLabel:
          'Grandchild',
        treeLabel:
          'Grandchild',
        relationshipClass:
          'structural',
        derivationKind:
          'structural-path',
        evidenceEdgeIds,
        connectionPath:
          path,
      });
    }

    const greatCount =
      down - 2;

    return descriptor({
      code:
        'great-grandchild',
      preciseLabel:
        greatLabel(
          greatCount,
          'Grandchild',
        ),
      treeLabel:
        greatLabel(
          greatCount,
          'Grandchild',
        ),
      relationshipClass:
        'structural',
      derivationKind:
        'structural-path',
      evidenceEdgeIds,
      connectionPath:
        path,
    });
  }

  if (
    up === 1 &&
    down === 1
  ) {
    return descriptor({
      code:
        'sibling',
      preciseLabel:
        'Sibling',
      treeLabel:
        'Sibling',
      relationshipClass:
        'structural',
      derivationKind:
        'structural-path',
      evidenceEdgeIds,
      connectionPath:
        path,
    });
  }

  if (
    up === 1 &&
    down >= 2
  ) {
    const greatCount =
      down - 2;

    return descriptor({
      code:
        greatCount === 0
          ? 'nibling'
          : 'great-nibling',
      preciseLabel:
        greatLabel(
          greatCount,
          'Niece / Nephew',
        ),
      treeLabel:
        greatLabel(
          greatCount,
          'Niece / Nephew',
        ),
      relationshipClass:
        'structural',
      derivationKind:
        'structural-path',
      evidenceEdgeIds,
      connectionPath:
        path,
    });
  }

  if (
    up >= 2 &&
    down === 1
  ) {
    const greatCount =
      up - 2;

    return descriptor({
      code:
        greatCount === 0
          ? 'aunt-uncle'
          : 'great-aunt-uncle',
      preciseLabel:
        greatLabel(
          greatCount,
          'Aunt / Uncle',
        ),
      treeLabel:
        greatLabel(
          greatCount,
          'Aunt / Uncle',
        ),
      relationshipClass:
        'structural',
      derivationKind:
        'structural-path',
      evidenceEdgeIds,
      connectionPath:
        path,
    });
  }

  if (
    up >= 2 &&
    down >= 2
  ) {
    const degree =
      Math.min(
        up,
        down,
      ) - 1;

    const removal =
      Math.abs(
        up - down,
      );

    return descriptor({
      code:
        'cousin',
      preciseLabel:
        `${ordinal(degree)} Cousin${removalLabel(removal)}`,
      treeLabel:
        'Cousin',
      relationshipClass:
        'structural',
      derivationKind:
        'structural-path',
      evidenceEdgeIds,
      connectionPath:
        path,
    });
  }

  return null;
};

const stepNiblingDescriptor = (
  descendantDepth: number,
  path:
    readonly CanonicalKinshipPathStep[],
): CanonicalKinshipDescriptor => {
  const greatCount =
    Math.max(
      0,
      descendantDepth - 1,
    );

  return descriptor({
    code:
      greatCount === 0
        ? 'step-nibling'
        : 'step-great-nibling',
    preciseLabel:
      `Step-${greatLabel(
        greatCount,
        'Niece / Nephew',
      )}`,
    treeLabel:
      greatLabel(
        greatCount,
        'Niece / Nephew',
      ),
    relationshipClass:
      'derived-step',
    derivationKind:
      'bounded-partnership-composition',
    affinityHopCount:
      1,
    evidenceEdgeIds:
      pathEdgeIds(path),
    connectionPath:
      path,
  });
};

const stepAuntUncleDescriptor = (
  ancestorDepth: number,
  path:
    readonly CanonicalKinshipPathStep[],
): CanonicalKinshipDescriptor => {
  const greatCount =
    Math.max(
      0,
      ancestorDepth - 1,
    );

  return descriptor({
    code:
      greatCount === 0
        ? 'step-aunt-uncle'
        : 'step-great-aunt-uncle',
    preciseLabel:
      `Step-${greatLabel(
        greatCount,
        'Aunt / Uncle',
      )}`,
    treeLabel:
      greatLabel(
        greatCount,
        'Aunt / Uncle',
      ),
    relationshipClass:
      'derived-step',
    derivationKind:
      'bounded-partnership-composition',
    affinityHopCount:
      1,
    evidenceEdgeIds:
      pathEdgeIds(path),
    connectionPath:
      path,
  });
};

const stepDescendantDescriptor = (
  structural:
    CanonicalKinshipDescriptor,
  path:
    readonly CanonicalKinshipPathStep[],
): CanonicalKinshipDescriptor => {
  const mapping:
    Readonly<Record<
      string,
      readonly [string, string, string]
    >> = {
      child: [
        'step-child',
        'Step-Child',
        'Child',
      ],
      grandchild: [
        'step-grandchild',
        'Step-Grandchild',
        'Grandchild',
      ],
      'great-grandchild': [
        'step-great-grandchild',
        `Step-${structural.preciseLabel}`,
        structural.treeLabel,
      ],
    };

  const value =
    mapping[structural.code];

  if (!value) {
    return descriptor({
      code:
        `step-${structural.code}`,
      preciseLabel:
        `Step-${structural.preciseLabel}`,
      treeLabel:
        structural.treeLabel,
      relationshipClass:
        'derived-step',
      derivationKind:
        'bounded-partnership-composition',
      affinityHopCount:
        1,
      evidenceEdgeIds:
        pathEdgeIds(path),
      connectionPath:
        path,
    });
  }

  return descriptor({
    code:
      value[0],
    preciseLabel:
      value[1],
    treeLabel:
      value[2],
    relationshipClass:
      'derived-step',
    derivationKind:
      'bounded-partnership-composition',
    affinityHopCount:
      1,
    evidenceEdgeIds:
      pathEdgeIds(path),
    connectionPath:
      path,
  });
};

const stepAncestorDescriptor = (
  structural:
    CanonicalKinshipDescriptor,
  path:
    readonly CanonicalKinshipPathStep[],
): CanonicalKinshipDescriptor => {
  const code =
    structural.code === 'parent'
      ? 'step-parent'
      : structural.code ===
          'grandparent'
        ? 'step-grandparent'
        : 'step-great-grandparent';

  return descriptor({
    code,
    preciseLabel:
      `Step-${structural.preciseLabel}`,
    treeLabel:
      structural.treeLabel,
    relationshipClass:
      'derived-step',
    derivationKind:
      'bounded-partnership-composition',
    affinityHopCount:
      1,
    evidenceEdgeIds:
      pathEdgeIds(path),
    connectionPath:
      path,
  });
};

const inLawDescriptor = (
  structural:
    CanonicalKinshipDescriptor,
  path:
    readonly CanonicalKinshipPathStep[],
): CanonicalKinshipDescriptor => {
  const special:
    Readonly<Record<
      string,
      readonly [string, string]
    >> = {
      parent: [
        'parent-in-law',
        'Parent-in-law',
      ],
      grandparent: [
        'grandparent-in-law',
        'Grandparent-in-law',
      ],
      sibling: [
        'sibling-in-law',
        'Sibling-in-law',
      ],
      child: [
        'child-in-law',
        'Child-in-law',
      ],
      grandchild: [
        'grandchild-in-law',
        'Grandchild-in-law',
      ],
      nibling: [
        'nibling-in-law',
        'Niece / Nephew-in-law',
      ],
      'aunt-uncle': [
        'aunt-uncle-in-law',
        'Aunt / Uncle-in-law',
      ],
    };

  const resolved =
    special[structural.code] ?? [
      `${structural.code}-in-law`,
      `${structural.preciseLabel}-in-law`,
    ];

  return descriptor({
    code:
      resolved[0],
    preciseLabel:
      resolved[1],
    treeLabel:
      structural.treeLabel,
    relationshipClass:
      'derived-in-law',
    derivationKind:
      'bounded-partnership-composition',
    affinityHopCount:
      1,
    evidenceEdgeIds:
      pathEdgeIds(path),
    connectionPath:
      path,
  });
};

const partnershipPathDescriptor = (
  path:
    readonly CanonicalKinshipPathStep[],
): CanonicalKinshipDescriptor | null => {
  const partnershipIndex =
    path.findIndex(
      step =>
        step.edgeKind ===
        'spouse-partner',
    );

  if (partnershipIndex < 0) {
    return null;
  }

  if (path.length === 1) {
    return descriptor({
      code:
        'spouse-partner',
      preciseLabel:
        'Spouse / Partner',
      treeLabel:
        'Spouse / Partner',
      relationshipClass:
        'active-partnership',
      derivationKind:
        'active-partnership-edge',
      affinityHopCount:
        1,
      evidenceEdgeIds:
        pathEdgeIds(path),
      connectionPath:
        path,
    });
  }

  if (partnershipIndex === 0) {
    const structural =
      structuralDescriptor(
        path.slice(1),
      );

    if (!structural) {
      return null;
    }

    if (
      structural.code === 'child' ||
      structural.code ===
        'grandchild' ||
      structural.code ===
        'great-grandchild'
    ) {
      return stepDescendantDescriptor(
        structural,
        path,
      );
    }

    return inLawDescriptor(
      structural,
      path,
    );
  }

  if (
    partnershipIndex ===
    path.length - 1
  ) {
    const structural =
      structuralDescriptor(
        path.slice(
          0,
          partnershipIndex,
        ),
      );

    if (!structural) {
      return null;
    }

    if (
      structural.code === 'parent' ||
      structural.code ===
        'grandparent' ||
      structural.code ===
        'great-grandparent'
    ) {
      return stepAncestorDescriptor(
        structural,
        path,
      );
    }

    return inLawDescriptor(
      structural,
      path,
    );
  }

  const prefix =
    path.slice(
      0,
      partnershipIndex,
    );

  const suffix =
    path.slice(
      partnershipIndex + 1,
    );

  /*
   * Reciprocal of:
   *
   *   sibling -> spouse -> descendant
   *
   * is:
   *
   *   descendant -> ancestor(s) -> spouse -> sibling
   *
   * This produces Step-Aunt / Uncle and deeper reciprocal variants.
   */
  if (
    prefix.length > 0 &&
    prefix.every(
      isReverseParentStep,
    ) &&
    suffix.length === 1 &&
    suffix[0].edgeKind ===
      'sibling'
  ) {
    return stepAuntUncleDescriptor(
      prefix.length,
      path,
    );
  }

  if (
    prefix.length !== 1 ||
    suffix.length === 0 ||
    !suffix.every(
      isForwardParentStep,
    )
  ) {
    return null;
  }

  if (
    prefix[0].edgeKind ===
    'sibling'
  ) {
    return stepNiblingDescriptor(
      suffix.length,
      path,
    );
  }

  if (
    isReverseParentStep(
      prefix[0],
    )
  ) {
    if (suffix.length === 1) {
      return descriptor({
        code:
          'step-sibling',
        preciseLabel:
          'Step-Sibling',
        treeLabel:
          'Sibling',
        relationshipClass:
          'derived-step',
        derivationKind:
          'bounded-partnership-composition',
        affinityHopCount:
          1,
        evidenceEdgeIds:
          pathEdgeIds(path),
        connectionPath:
          path,
      });
    }

    return stepNiblingDescriptor(
      suffix.length - 1,
      path,
    );
  }

  return null;
};

const directEdgeConnectionPath = (
  viewerId: string,
  memberId: string,
  edge: CanonicalTreeRelationshipEdge,
): readonly CanonicalKinshipPathStep[] | null => {
  const viewerIsSource =
    edge.sourceMemberId === viewerId &&
    edge.targetMemberId === memberId;

  const viewerIsTarget =
    edge.targetMemberId === viewerId &&
    edge.sourceMemberId === memberId;

  if (
    !viewerIsSource &&
    !viewerIsTarget
  ) {
    return null;
  }

  const traversal:
    CanonicalKinshipPathTraversal =
      edge.direction ===
      'child-mediated'
        ? 'child-mediated'
        : edge.direction ===
            'symmetric'
          ? 'symmetric'
          : viewerIsSource
            ? 'forward'
            : 'reverse';

  return freezePath([
    {
      fromMemberId:
        viewerId,
      toMemberId:
        memberId,
      edgeId:
        edge.id,
      edgeKind:
        edge.kind,
      traversal,
    },
  ]);
};

const directEdgeDescriptor = (
  edge: CanonicalTreeRelationshipEdge,
  viewerId: string,
  memberId: string,
): CanonicalKinshipDescriptor | null => {
  if (
    edge.confirmation === 'rejected'
  ) {
    return null;
  }

  const connectionPath =
    directEdgeConnectionPath(
      viewerId,
      memberId,
      edge,
    );

  if (!connectionPath) {
    return null;
  }

  if (
    edge.kind ===
    'spouse-partner'
  ) {
    if (
      edge.status !== 'active' ||
      !edge.rendersActivePartnership
    ) {
      return null;
    }

    return descriptor({
      code:
        'spouse-partner',
      preciseLabel:
        'Spouse / Partner',
      treeLabel:
        'Spouse / Partner',
      relationshipClass:
        'active-partnership',
      derivationKind:
        'active-partnership-edge',
      affinityHopCount:
        1,
      evidenceEdgeIds:
        [edge.id],
      connectionPath,
    });
  }

  if (
    edge.kind === 'co-parent'
  ) {
    if (
      edge.sharedChildIds.length === 0
    ) {
      return null;
    }

    return descriptor({
      code:
        'co-parent',
      preciseLabel:
        'Co-Parent',
      treeLabel:
        'Co-Parent',
      relationshipClass:
        'co-parent',
      derivationKind:
        'direct-governed-edge',
      evidenceEdgeIds:
        [edge.id],
      connectionPath,
    });
  }

  if (
    !isCanonicalKinshipTerminalEdgeKind(
      edge.kind,
    )
  ) {
    return null;
  }

  const labelByKind:
    Readonly<Record<string, string>> = {
      'step-family':
        'Step Family',
      'in-law':
        'In-Law',
      guardian:
        'Guardian',
      'foster-family':
        'Foster Family',
      'chosen-family':
        'Chosen Family',
      'god-family':
        'God Family',
      'affiliated-family':
        'Affiliated Family',
    };

  const label =
    edge.preferredFamilyTitle ??
    labelByKind[edge.kind] ??
    'Family';

  return descriptor({
    code:
      edge.kind,
    preciseLabel:
      label,
    treeLabel:
      label,
    relationshipClass:
      'direct-affiliation',
    derivationKind:
      'direct-governed-edge',
    evidenceEdgeIds:
      [edge.id],
    connectionPath,
  });
};


const withHalfSiblingTruth = (
  candidate:
    CanonicalKinshipDescriptor,
  viewerId: string,
  memberId: string,
  indexes:
    CanonicalKinshipSemanticIndexes,
): CanonicalKinshipDescriptor => {
  /*
   * The current canonical facts expose observed parent edges, but no
   * governed assertion that either person's observed parent set is
   * complete.
   *
   * Absence of another observed parent therefore cannot prove
   * half-sibling status. Preserve the supported structural sibling truth
   * until separately governed parent-set-completeness authority exists.
   */
  void viewerId;
  void memberId;
  void indexes;

  return candidate;
}

const sharedParentageCoParentDescriptor = (
  viewerId: string,
  memberId: string,
  indexes:
    CanonicalKinshipSemanticIndexes,
): CanonicalKinshipDescriptor | null => {
  if (viewerId === memberId) {
    return null;
  }

  const viewerChildren =
    indexes.childIdsByParent.get(
      viewerId,
    ) ?? [];

  const memberChildSet =
    new Set(
      indexes.childIdsByParent.get(
        memberId,
      ) ?? [],
    );

  const sharedChildren =
    viewerChildren
      .filter(
        childId =>
          memberChildSet.has(
            childId,
          ),
      )
      .sort();

  if (sharedChildren.length === 0) {
    return null;
  }

  const evidenceEdgeIds:
    string[] = [];

  for (
    const childId of sharedChildren
  ) {
    const viewerEdge =
      indexes.parentEdgeIdByPair.get(
        parentPairKey(
          viewerId,
          childId,
        ),
      );

    const memberEdge =
      indexes.parentEdgeIdByPair.get(
        parentPairKey(
          memberId,
          childId,
        ),
      );

    if (viewerEdge) {
      evidenceEdgeIds.push(
        viewerEdge,
      );
    }

    if (memberEdge) {
      evidenceEdgeIds.push(
        memberEdge,
      );
    }
  }

  const childId =
    sharedChildren[0];

  const viewerEdge =
    indexes.parentEdgeIdByPair.get(
      parentPairKey(
        viewerId,
        childId,
      ),
    );

  const memberEdge =
    indexes.parentEdgeIdByPair.get(
      parentPairKey(
        memberId,
        childId,
      ),
    );

  const path:
    CanonicalKinshipPathStep[] =
      viewerEdge &&
      memberEdge
        ? [
            {
              fromMemberId:
                viewerId,
              toMemberId:
                childId,
              edgeId:
                viewerEdge,
              edgeKind:
                'parent-child',
              traversal:
                'forward',
            },
            {
              fromMemberId:
                childId,
              toMemberId:
                memberId,
              edgeId:
                memberEdge,
              edgeKind:
                'parent-child',
              traversal:
                'reverse',
            },
          ]
        : [];

  return descriptor({
    code:
      'co-parent',
    preciseLabel:
      'Co-Parent',
    treeLabel:
      'Co-Parent',
    relationshipClass:
      'co-parent',
    derivationKind:
      'shared-parentage',
    evidenceEdgeIds,
    connectionPath:
      path,
  });
};

const descriptorPriority = (
  value:
    CanonicalKinshipDescriptor,
): number => {
  switch (
    value.relationshipClass
  ) {
    case 'self':
      return 0;
    case 'structural':
      return 1;
    case 'active-partnership':
      return 2;
    case 'derived-step':
    case 'derived-in-law':
      return 3;
    case 'co-parent':
      return 4;
    case 'direct-affiliation':
      return 5;
    case 'unknown':
    default:
      return 6;
  }
};

const compareDescriptors = (
  left:
    CanonicalKinshipDescriptor,
  right:
    CanonicalKinshipDescriptor,
): number => {
  const priority =
    descriptorPriority(left) -
    descriptorPriority(right);

  if (priority !== 0) {
    return priority;
  }

  const code =
    left.code.localeCompare(
      right.code,
    );

  if (code !== 0) {
    return code;
  }

  const pathLength =
    left.connectionPath.length -
    right.connectionPath.length;

  if (pathLength !== 0) {
    return pathLength;
  }

  return left.evidenceEdgeIds
    .join('|')
    .localeCompare(
      right.evidenceEdgeIds.join(
        '|',
      ),
    );
};

const mergeDuplicateFacet = (
  left:
    CanonicalKinshipDescriptor,
  right:
    CanonicalKinshipDescriptor,
): CanonicalKinshipDescriptor => {
  const representative =
    compareDescriptors(
      left,
      right,
    ) <= 0
      ? left
      : right;

  return descriptor({
    code:
      representative.code,
    preciseLabel:
      representative.preciseLabel,
    treeLabel:
      representative.treeLabel,
    relationshipClass:
      representative.relationshipClass,
    derivationKind:
      representative.derivationKind,
    affinityHopCount:
      representative.affinityHopCount,
    evidenceEdgeIds:
      [
        ...left.evidenceEdgeIds,
        ...right.evidenceEdgeIds,
      ],
    connectionPath:
      representative.connectionPath,
  });
};

const preciseFacetIdentityKey = (
  value:
    CanonicalKinshipDescriptor,
): string =>
  JSON.stringify([
    value.code,
    value.preciseLabel,
    value.relationshipClass,
    value.affinityHopCount,
  ]);

export const
canonicalKinshipProcessingGrammarIdentityForPath = (
  path:
    readonly CanonicalKinshipPathStep[],
): string =>
  JSON.stringify(
    path.map(
      step =>
        Object.freeze([
          step.edgeKind,
          step.traversal,
        ]),
    ),
  );

export const
canonicalKinshipSemanticRouteIdentityForPath = (
  path:
    readonly CanonicalKinshipPathStep[],
): string => {
  if (
    !isCanonicalKinshipTraversalPathAdmissible(
      path,
    )
  ) {
    return JSON.stringify([
      'inadmissible',
    ]);
  }

  const terminalStep =
    path[path.length - 1];

  if (
    terminalStep &&
    isCanonicalKinshipTerminalEdgeKind(
      terminalStep.edgeKind,
    )
  ) {
    const structuralPrefix =
      path.slice(
        0,
        path.length - 1,
      );

    const prefixDescriptor =
      structuralDescriptor(
        structuralPrefix,
      );

    return JSON.stringify([
      'terminal-affiliation',
      terminalStep.edgeKind,
      terminalStep.traversal,
      prefixDescriptor
        ? preciseFacetIdentityKey(
            prefixDescriptor,
          )
        : canonicalKinshipProcessingGrammarIdentityForPath(
            structuralPrefix,
          ),
    ]);
  }

  const hasPartnership =
    path.some(
      step =>
        step.edgeKind ===
        'spouse-partner',
    );

  const routeDescriptor =
    hasPartnership
      ? partnershipPathDescriptor(
          path,
        )
      : structuralDescriptor(
          path,
        );

  if (routeDescriptor) {
    return JSON.stringify([
      'precise-facet',
      preciseFacetIdentityKey(
        routeDescriptor,
      ),
    ]);
  }

  return JSON.stringify([
    'admissible-grammar',
    canonicalKinshipProcessingGrammarIdentityForPath(
      path,
    ),
  ]);
};

const unknownDescriptor = (
  path:
    readonly CanonicalKinshipPathStep[],
): CanonicalKinshipDescriptor =>
  descriptor({
    code:
      'unknown',
    preciseLabel:
      'Unresolved Relationship',
    treeLabel:
      'Family',
    relationshipClass:
      'unknown',
    derivationKind:
      'unresolved',
    affinityHopCount:
      path.filter(
        step =>
          step.edgeKind ===
          'spouse-partner',
      ).length,
    evidenceEdgeIds:
      pathEdgeIds(path),
    connectionPath:
      path,
  });

export const
deriveCanonicalKinshipSemantics = (
  input:
    DeriveCanonicalKinshipSemanticsInput,
): CanonicalKinshipSemantics => {
  const candidates:
    CanonicalKinshipDescriptor[] = [];

  for (
    const path of
      input.connectionPaths
  ) {
    if (
      !isCanonicalKinshipTraversalPathAdmissible(
        path,
      )
    ) {
      continue;
    }

    const hasPartnership =
      path.some(
        step =>
          step.edgeKind ===
          'spouse-partner',
      );

    const pathDescriptor =
      hasPartnership
        ? partnershipPathDescriptor(
            path,
          )
        : structuralDescriptor(
            path,
          );

    if (pathDescriptor) {
      candidates.push(
        withHalfSiblingTruth(
          pathDescriptor,
          input.viewerId,
          input.memberId,
          input.indexes,
        ),
      );
    }
  }

  const pair =
    canonicalTreeEdgePairKey(
      input.viewerId,
      input.memberId,
    );

  const directEdges =
    input.indexes
      .directEdgesByPair
      .get(pair) ?? [];

  for (const edge of directEdges) {
    const direct =
      directEdgeDescriptor(
        edge,
        input.viewerId,
        input.memberId,
      );

    if (direct) {
      candidates.push(direct);
    }
  }

  const coParent =
    sharedParentageCoParentDescriptor(
      input.viewerId,
      input.memberId,
      input.indexes,
    );

  if (coParent) {
    candidates.push(coParent);
  }

  if (candidates.length === 0) {
    candidates.push(
      unknownDescriptor(
        input.connectionPaths[0] ??
          [],
      ),
    );
  }

  const byPreciseIdentity =
    new Map<
      string,
      CanonicalKinshipDescriptor
    >();

  for (const candidate of candidates) {
    const identity =
      preciseFacetIdentityKey(
        candidate,
      );

    const existing =
      byPreciseIdentity.get(
        identity,
      );

    byPreciseIdentity.set(
      identity,
      existing
        ? mergeDuplicateFacet(
            existing,
            candidate,
          )
        : candidate,
    );
  }

  const facets =
    Object.freeze(
      [...byPreciseIdentity.values()].sort(
        compareDescriptors,
      ),
    );

  return Object.freeze({
    ruleVersion:
      CANONICAL_KINSHIP_RULE_VERSION,
    primary:
      facets[0],
    facets,
  });
};
